import React, { useState } from "react";
import { Trash2, Upload, Paperclip, Maximize2 } from "react-feather";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import AttachmentPreviewModal from "../../../components/common/AttachmentPreviewModal";
import InlineFilePreview from "./InlineFilePreview";
import {
  useDeleteVoucherAttachmentMutation,
  useGetVoucherAttachmentContentQuery,
  useLazyGetVoucherAttachmentContentQuery,
  useListVoucherAttachmentsQuery,
  useUploadVoucherAttachmentMutation,
} from "../pettyCashApi";

export const ATTACHMENT_KINDS = [
  { value: "CHEQUE", label: "Cheque" },
  { value: "BANK_SLIP", label: "Bank slip" },
  { value: "INVOICE", label: "Invoice" },
  { value: "OTHER", label: "Other" },
];
const KIND_COLOR = { CHEQUE: "#1F5FAD", BANK_SLIP: "#0B7A55", INVOICE: "#B0570B", OTHER: "#4A5A6A" };
const kindLabel =(k) => ATTACHMENT_KINDS.find((x) => x.value === k)?.label || k || "Other";
export const MAX_ATTACHMENT_BYTES = 1024 * 1024;
export const ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png,.pdf";
const formatBytes = (bytes) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(0)} KB`);

/**
 * Upload one or more files against a voucher, one call per file (the API takes one
 * file per request). Shared by the Pay panel (after the payment goes through) and the
 * Attachments card. Reports how many failed rather than throwing.
 */
export const useUploadVoucherAttachments = () => {
  const [upload, { isLoading }] = useUploadVoucherAttachmentMutation();
  const uploadAll = async (recordGuid, files, kind) => {
    let failed = 0;
    for (const file of files) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await upload({ recordGuid, file, kind }).unwrap();
      } catch (error) {
        failed += 1;
        toast.error(`${file.name}: ${error?.message || "could not upload"}`);
      }
    }
    return { uploaded: files.length - failed, failed };
  };
  return { uploadAll, isUploading: isLoading };
};

// Checks the browser can do before sending: type and size, per file.
export const rejectBadAttachmentFiles = (files) => {
  const ok = [];
  for (const f of files) {
    if (!["image/jpeg", "image/png", "application/pdf"].includes(f.type)) toast.error(`${f.name}: only JPEG, PNG or PDF`);
    else if (f.size > MAX_ATTACHMENT_BYTES) toast.error(`${f.name}: must be 1 MB or smaller`);
    else ok.push(f);
  }
  return ok;
};

// Every attachment shows itself straight away - picture, or the PDF's first page
// (2026-09-30: "the attachment should be visible by default, without clicking the view
// button"). The bytes are fetched once per attachment and kept by RTK for ten minutes.
const AttachmentThumb = ({ recordGuid, attachment, onOpen }) => {
  const { data: blob } = useGetVoucherAttachmentContentQuery({ recordGuid, attachmentRecordGuid: attachment.recordGuid });
  return <InlineFilePreview blob={blob} fileName={attachment.originalFileName} onOpen={onOpen} width={180} height={220} />;
};

/**
 * The voucher's supporting documents (Batch198): cheque image, bank slip, invoice, other.
 * Separate from the single receipt. Images render as thumbnails in the card itself,
 * PDFs as a file tile; either opens in the preview modal. Available at any status, so
 * a cheque scanned after payment still lands on the voucher.
 */
const VoucherAttachments = ({ recordGuid, canManage }) => {
  const { data: attachments = [], isLoading } = useListVoucherAttachmentsQuery(recordGuid);
  const { uploadAll, isUploading } = useUploadVoucherAttachments();
  const [deleteAttachment] = useDeleteVoucherAttachmentMutation();
  const [fetchContent] = useLazyGetVoucherAttachmentContentQuery();
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState("CHEQUE");
  const [preview, setPreview] = useState(null); // { blob, attachment }

  const handleUpload = async () => {
    if (files.length === 0) return toast.error("Choose one or more files first");
    const { uploaded } = await uploadAll(recordGuid, files, kind);
    if (uploaded > 0) toast.success(`${uploaded} document${uploaded === 1 ? "" : "s"} attached`);
    setFiles([]);
    const input = document.getElementById(`voucher-attach-${recordGuid}`);
    if (input) input.value = "";
  };

  const handleOpen = async (attachment) => {
    try {
      const blob = await fetchContent({ recordGuid, attachmentRecordGuid: attachment.recordGuid }).unwrap();
      setPreview({ blob, attachment });
    } catch (error) {
      toast.error(error?.message || "Could not open the document");
    }
  };

  const handleDelete = async (attachment) => {
    if (!window.confirm(`Remove "${attachment.originalFileName}"?`)) return;
    try {
      await deleteAttachment({ recordGuid, attachmentRecordGuid: attachment.recordGuid }).unwrap();
      toast.success("Document removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove the document");
    }
  };

  return (
    <div className="card vd-card mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <div className="vd-card-title mb-1">
              <Paperclip size={16} /> Supporting Documents
              {attachments.length > 0 && <span className="badge rounded-pill fw-normal" style={{ background: "#6E1423" }}>{attachments.length}</span>}
            </div>
            <p className="text-muted small mb-0">Cheque image, bank slip, invoice or anything else supporting this voucher. The receipt is kept separately above.</p>
          </div>
        </div>

        {canManage && (
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-center p-2 rounded" style={{ border: "1px dashed #d8b9be", background: "#fdf7f8" }}>
            <select className="form-control form-control-sm" style={{ width: 130 }} value={kind} onChange={(e) => setKind(e.target.value)} disabled={isUploading}>
              {ATTACHMENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            <input
              id={`voucher-attach-${recordGuid}`}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="form-control form-control-sm"
              style={{ maxWidth: 340 }}
              onChange={(e) => setFiles(rejectBadAttachmentFiles([...(e.target.files || [])]))}
              disabled={isUploading}
            />
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleUpload} disabled={isUploading || files.length === 0}>
              <Upload size={14} className="me-1" /> {isUploading ? "Uploading…" : `Attach${files.length > 1 ? ` ${files.length} files` : ""}`}
            </button>
            <small className="text-muted">JPEG, PNG or PDF, up to 1 MB each. Several files can be picked at once.</small>
          </div>
        )}

        {isLoading && <p className="text-muted small mb-0">Loading…</p>}
        {!isLoading && attachments.length === 0 && <p className="text-muted small mb-0">No documents attached.</p>}

        {attachments.length > 0 && (
          <div className="d-flex flex-wrap gap-3">
            {attachments.map((a) => (
              <div key={a.recordGuid} style={{ width: 180 }}>
                <AttachmentThumb recordGuid={recordGuid} attachment={a} onOpen={() => handleOpen(a)} />
                <div className="small mt-1 d-flex justify-content-between align-items-start gap-1">
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold" style={{ color: KIND_COLOR[a.attachmentKind] || KIND_COLOR.OTHER }}>{kindLabel(a.attachmentKind)}</div>
                    <div className="text-truncate text-muted" title={a.originalFileName}>{a.originalFileName}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {a.uploadedByName || "-"} · {a.uploadedDate ? dayjs(a.uploadedDate).format("DD MMM YYYY") : ""} · {formatBytes(a.fileSizeBytes)}
                    </div>
                  </div>
                  <div className="d-flex flex-column">
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => handleOpen(a)} title="Open full size"><Maximize2 size={14} /></button>
                    {canManage && (
                      <button type="button" className="btn btn-link btn-sm p-0 text-danger" onClick={() => handleDelete(a)} title="Remove"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AttachmentPreviewModal
        show={Boolean(preview)}
        onClose={() => setPreview(null)}
        blob={preview?.blob}
        fileName={preview?.attachment.originalFileName}
        title={preview ? `${kindLabel(preview.attachment.attachmentKind)} - ${preview.attachment.originalFileName}` : ""}
      />
    </div>
  );
};

export default VoucherAttachments;
